import {DataStatus} from 'platform/components';
import {DocumentPreview, useDocumentPreview} from 'platform/document-preview';
import {Box, HStack, Integer, useAvailableHeight} from 'platform/foundation';

import {useEffect} from 'react';

import {isNotNil} from 'ramda';

import {
  useExtractImagesFromZipQuery,
  useGetMessageByIdQuery,
  useGetOriginalFileUrlQuery,
} from '@dms/api/core';

import {
  EXTRACT_DELIVERY_NOTE_DOCUMENT,
  ExtractDeliveryNoteDocumentMessageInput,
  ExtractDeliveryNoteDocumentResult,
  UseCaseType,
} from '@prompt-bridge/shared/validations';

import {getRandomId} from 'shared/web';

import {DeliveryNoteDocumentPreviewForm} from './DeliveryNoteDocumentPreviewForm';

interface DeliveryNoteDocumentPreviewProps {
  messageId: string;
  onClose: VoidFunction;
}

export function DeliveryNoteDocumentPreview(props: DeliveryNoteDocumentPreviewProps) {
  const {availableHeightRef, availableHeight} = useAvailableHeight<HTMLDivElement>();

  const availableHeightWithoutPadding = (availableHeight -
    DIALOG_TOP_PADDING -
    DIALOG_BOTTOM_PADDING -
    DIALOG_BOTTOM_MARGIN) as Integer;

  const {
    data: document,
    isLoading: isDocumentLoading,
    isError: hasDocumentError,
  } = useGetMessageByIdQuery({id: props.messageId}, {refetchOnMountOrArgChange: true});

  const documentInput = isDeliveryNote(document?.useCaseType)
    ? (document?.input as ExtractDeliveryNoteDocumentMessageInput)
    : null;

  const hasFileId = isNotNil(documentInput?.fileId);

  const hasZipId = isNotNil(documentInput?.zipId);

  const {
    data: singleFileUrl,
    isLoading: isSingleFileUrlLoading,
    isError: hasSingleFileUrlError,
  } = useGetOriginalFileUrlQuery(
    {fileId: documentInput?.fileId as string, namespace: 'prompt-bridge'},
    {skip: isDocumentLoading || !hasFileId}
  );

  const {
    data: imagesFromZip,
    isLoading: areImagesFromZipLoading,
    isError: hasImagesFromZipError,
  } = useExtractImagesFromZipQuery(
    {zipId: documentInput?.zipId as string, namespace: 'prompt-bridge'},
    {skip: isDocumentLoading || !hasZipId}
  );

  const isLoading = isDocumentLoading || isSingleFileUrlLoading || areImagesFromZipLoading;

  const hasError = hasDocumentError || hasSingleFileUrlError || hasImagesFromZipError;

  const documentPreview = useDocumentPreview();

  const documentResult = isDeliveryNote(document?.useCaseType)
    ? (document?.result?.result as ExtractDeliveryNoteDocumentResult)
    : null;

  useEffect(() => {
    if (!isLoading && isNotNil(imagesFromZip)) {
      const pages = imagesFromZip.images.map((image, index) => ({
        id: getRandomId(),
        url: image.base64,
        pageNumber: index + 1,
      }));
      return documentPreview.controls.setPages(pages);
    }

    if (!isLoading && isNotNil(singleFileUrl)) {
      const pages = [
        {
          id: getRandomId(),
          url: singleFileUrl,
          pageNumber: 1,
        },
      ];
      return documentPreview.controls.setPages(pages);
    }
    // We do not include document.controls because it causes infinite loop.
    // This object is stable, therefore it does not need to be included.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, imagesFromZip, singleFileUrl]);

  return (
    <Box ref={availableHeightRef} height={availableHeightWithoutPadding}>
      <DataStatus isLoading={isLoading} isError={hasError} minHeight="100%">
        <HStack width="100%" height="100%" justify="center" align="center">
          <DocumentPreview documentPreview={documentPreview} />
          <DeliveryNoteDocumentPreviewForm
            extractionId={document?.id}
            documentPreview={documentPreview}
            documentInput={documentInput}
            documentResult={documentResult}
            onClose={props.onClose}
          />
        </HStack>
      </DataStatus>
    </Box>
  );
}

const DIALOG_TOP_PADDING: Integer = 4;
const DIALOG_BOTTOM_PADDING: Integer = 4;
const DIALOG_BOTTOM_MARGIN: Integer = 16;

const isDeliveryNote = (result: UseCaseType | undefined): boolean =>
  result === EXTRACT_DELIVERY_NOTE_DOCUMENT;
